import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect } from 'react';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { AppContext } from '../containers/appContextContainer';
import {
    SoeknadstypeWeb,
    UploadStatusWeb,
    VedleggContainerWeb,
    VedleggDeleteWeb,
    VedleggStatusWeb,
    VedleggWeb,
} from '../generated-types';
import { uploadVedlegg } from '../services/vedleggService';
import errorTypes from '../util/gtm/errorTypes';
import restClient from '../util/web-http-client';

export const useVedlegg = (soeknadtype: SoeknadstypeWeb) => {
    const { setError } = useContext(AppContext);
    const query = useQuery({
        queryKey: ['vedlegg'],
        queryFn: () => restClient.getVedlegg(soeknadtype),
        retry: 1,
        refetchOnMount: 'always',
    });

    useEffect(() => {
        if (query.error) {
            setError({
                errorType: errorTypes.GET_VEDLEGG,
                errorException: query.error as RequestExceptionWithResponse,
            });
        }
    }, [query.error, setError]);

    return query;
};

export const useUploadVedlegg = (
    vedleggContainer: VedleggContainerWeb | undefined,
    successCallback?: (allUploaded: boolean) => void,
) => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['uploadVedlegg'],
        mutationFn: (formData: FormData) => uploadVedlegg(formData),
        onSuccess: (data: VedleggStatusWeb) => {
            if (data.status === UploadStatusWeb.OK && vedleggContainer) {
                vedleggContainer.vedlegg.push({
                    dokumentNavn: data.dokumentNavn,
                    kategori: data.kategori,
                    fileType: data.fileType,
                    mimeType: data.mimeType,
                    size: data.size,
                    uploaded: data.uploaded,
                });
                queryClient.setQueryData(['vedlegg'], vedleggContainer);
            }
            if (successCallback) {
                const uploading = vedleggContainer?.vedlegg.some((v) => !v.uploaded);
                successCallback(!uploading);
            }
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.UPLOAD_VEDLEGG, errorException: error as RequestExceptionWithResponse });
        },
    });
};

export const useDeleteVedlegg = () => {
    const { setError } = useContext(AppContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationKey: ['deleteVedlegg'],
        mutationFn: (vedleggDelete: VedleggDeleteWeb) => restClient.deleteVedlegg(vedleggDelete),
        onSuccess: (data: VedleggDeleteWeb) => {
            const vedleggContainer = queryClient.getQueryData<VedleggContainerWeb>(['vedlegg']);
            vedleggContainer?.vedlegg.splice(
                vedleggContainer?.vedlegg.findIndex((v: VedleggWeb) => v.dokumentNavn === data.dokumentNavn),
                1,
            );
            queryClient.setQueryData(['vedlegg'], vedleggContainer);
        },
        onError: (error: unknown) => {
            setError({ errorType: errorTypes.DELETE_VEDLEGG, errorException: error as RequestExceptionWithResponse });
        },
    });
};
